import type { InputAction } from '../types';

// ============================================
// InputManager - Gestión de entrada del jugador
// ============================================

export class InputManager {
  private keys: Map<string, boolean> = new Map();
  private mouseMovement = { x: 0, y: 0 };
  private pointerLocked = false;
  private action: InputAction = {
    forward: false,
    backward: false,
    left: false,
    right: false,
    jump: false,
    crouch: false,
    interact: false,
    run: false,
  };

  private boundKeyDown: (e: KeyboardEvent) => void;
  private boundKeyUp: (e: KeyboardEvent) => void;
  private boundMouseMove: (e: MouseEvent) => void;
  private boundPointerLockChange: () => void;

  constructor() {
    this.boundKeyDown = this.onKeyDown.bind(this);
    this.boundKeyUp = this.onKeyUp.bind(this);
    this.boundMouseMove = this.onMouseMove.bind(this);
    this.boundPointerLockChange = this.onPointerLockChange.bind(this);
  }

  init(): void {
    document.addEventListener('keydown', this.boundKeyDown);
    document.addEventListener('keyup', this.boundKeyUp);
    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('pointerlockchange', this.boundPointerLockChange);

    // Solicitar pointer lock al hacer clic
    document.addEventListener('click', () => {
      if (!this.isPointerLocked) {
        document.body.requestPointerLock();
      }
    });

    console.log('[InputManager] Inicializado');
  }

  private onKeyDown(e: KeyboardEvent): void {
    this.keys.set(e.code, true);
    this.updateActions();
  }

  private onKeyUp(e: KeyboardEvent): void {
    this.keys.set(e.code, false);
    this.updateActions();
  }

  private onMouseMove(e: MouseEvent): void {
    if (this.pointerLocked) {
      this.mouseMovement.x += e.movementX;
      this.mouseMovement.y += e.movementY;
    }
  }

  private onPointerLockChange(): void {
    this.pointerLocked = document.pointerLockElement === document.body;
  }

  private updateActions(): void {
    this.action.forward = this.keys.get('KeyW') || this.keys.get('ArrowUp') || false;
    this.action.backward = this.keys.get('KeyS') || this.keys.get('ArrowDown') || false;
    this.action.left = this.keys.get('KeyA') || this.keys.get('ArrowLeft') || false;
    this.action.right = this.keys.get('KeyD') || this.keys.get('ArrowRight') || false;
    this.action.jump = this.keys.get('Space') || false;
    this.action.crouch = this.keys.get('KeyC') || this.keys.get('ControlLeft') || false;
    this.action.interact = this.keys.get('KeyE') || false;
    this.action.run = this.keys.get('ShiftLeft') || this.keys.get('ShiftRight') || false;
  }

  getActions(): InputAction {
    return { ...this.action };
  }

  getMouseMovement(): { x: number; y: number } {
    const movement = { ...this.mouseMovement };
    this.mouseMovement.x = 0;
    this.mouseMovement.y = 0;
    return movement;
  }

  isPointerLocked(): boolean {
    return this.pointerLocked;
  }

  isKeyPressed(code: string): boolean {
    return this.keys.get(code) || false;
  }

  dispose(): void {
    document.removeEventListener('keydown', this.boundKeyDown);
    document.removeEventListener('keyup', this.boundKeyUp);
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('pointerlockchange', this.boundPointerLockChange);
  }
}
